/**
 * @module account reducer
 *
 * Stores all data for the currently-logged-in user's account. Contains all
 * profile data, as well as this user's company data. Includes other "global"
 * data for an account like `isAdmin` (which represents superadmin status),
 * account dates, and `userId`.
 *
 * @example account state
 * {
 *   userId: 1,
 *   name: 'Ariana Admin',
 *   lang: 'en-ca',
 *   email: 'ariana@admin.com',
 *   firstName: 'Ariana',
 *   lastName: 'Admin',
 *   name: 'Ariana Admin',
 *   avatarUrl: 'http://someplace.com/ariana_avatar.jpg',
 *   companyId: 123,
 *   companyColor: '#f00',
 *   companyLogoUrl: 'https://someplace.com/logo.png',
 *   companyLang: 'en-ca',
 *   isAdmin: false, // Note: isAdmin represents SUPERADMIN status (worktangoAdmin in DB), not company admin
 *   loginAt: '2020-10-11 08:09:29',
 *   createdAt: '2020-10-11 08:09:29'
 * }
 */
import { createReducer } from '@reduxjs/toolkit';
import { userActions } from '../actions/user.actions';
import { metaActions } from '../actions/meta.actions';
import { companyActions } from '../actions/company.actions';
import { accountActions } from '../actions/account.actions';
import { defaultLang } from '~/config/settings/app.settings';

const defaultState = {
  userId: null,
  email: '',
  lang: defaultLang,
  supportedLanguages: [],
  name: '',
  firstName: '',
  lastName: '',
  avatarUrl: '',
  isAdmin: false,
  isImpersonating: false,
  loginAt: null,
  updatedAt: null,
  createdAt: null,
  companyId: null,
  companyColor: '#fff',
  companyLogoUrl: '',
  companyName: '',
  companyFeatureSettings: {},
};

const reset = () => defaultState;

const mergeAccount = (state, action) => {
  return {
    ...state,
    ...action.payload.account,
  };
};

const modifyProfile = (state, action) => {
  const { userId, name, firstName, lastName, lang, langId, avatarUrl } = action.payload;

  // Only update values that have changed.
  if (userId) state.userId = userId;
  if (firstName) state.firstName = firstName;
  if (lastName) state.lastName = lastName;
  if (lang) state.lang = lang;
  if (langId) state.langId = langId;
  if (avatarUrl) state.avatarUrl = avatarUrl;
  if (name) state.name = name;
};

// If the account user's company has been modified, also update the account
// values to keep them in sync with the latest updates.
const changeCompany = (state, action) => {
  const accountCompany = action?.payload?.entities?.companies?.[state.companyId];

  if (accountCompany?.name) state.companyName = accountCompany.name;
  if (accountCompany?.color) state.companyColor = accountCompany.color;
  if (accountCompany?.logoUrl) state.companyLogoUrl = accountCompany.logoUrl;
  if (accountCompany?.supportedLanguages) state.supportedLanguages = accountCompany.supportedLanguages;
  if (accountCompany?.companyFeatureSettings) state.companyFeatureSettings = accountCompany.companyFeatureSettings;
};

const changeLanguage = (state, action) => {
  state.lang = action.payload.lang;
};

const changeAvatar = (state, action) => {
  if (action.payload.avatarUrl) state.avatarUrl = action.payload.avatarUrl;
};

const updateLogo = (state, action) => {
  if (action.payload.logoUrl) state.companyLogoUrl = action.payload.logoUrl;
};

const account = createReducer(defaultState, (builder) => {
  builder.addCase(metaActions.reset, reset);
  builder.addCase(accountActions.logout.pending, reset);
  builder.addCase(accountActions.fetchAccount.fulfilled, mergeAccount);
  builder.addCase(companyActions.fetchCompany.fulfilled, changeCompany);
  builder.addCase(userActions.changeProfile.fulfilled, modifyProfile);
  builder.addCase(userActions.changeLanguage, changeLanguage);
  builder.addCase(userActions.changeAvatar.fulfilled, changeAvatar);
  builder.addCase(companyActions.modifyCompany.fulfilled, changeCompany);
  builder.addCase(companyActions.changeLogo.fulfilled, updateLogo);
});

export default account;
